import { useEffect, useId, useRef, useState, type ChangeEvent } from 'react';
import { Button } from '../atoms/Button/Button';
import { Checkbox } from '../atoms/Checkbox/Checkbox';
import { Grid } from '../atoms/Grid/Grid';
import { Input } from '../atoms/Input/Input';
import { Select } from '../atoms/Select/Select';
import { JOURNAL_TAGS, journalTagLabel } from '../journal/journalTags';
import { useTranslation } from '../../i18n/useTranslation';
import {
  countActiveFeedFilters,
  isFeedRangeInvalid,
  type FeedFilterState,
  type FeedPeriod,
  type FeedSort,
} from './useCommunityFeedFilters';
import './CommunityFeedFilters.css';

/** Wait this long after the last keystroke before filtering (one request per pause). */
export const FEED_FILTER_DEBOUNCE_MS = 300;

export interface CommunityFeedFiltersProps {
  value: FeedFilterState;
  onChange: (patch: Partial<Omit<FeedFilterState, 'page'>>) => void;
  onReset: () => void;
}

/** A text filter that writes to the URL only after a typing pause, and follows outside changes (reset, Back). */
function useDebouncedText(external: string, commit: (value: string) => void) {
  const [text, setText] = useState(external);
  const commitRef = useRef(commit);
  commitRef.current = commit;
  const lastCommitted = useRef(external);

  useEffect(() => {
    if (external !== lastCommitted.current) {
      lastCommitted.current = external;
      setText(external);
    }
  }, [external]);

  useEffect(() => {
    if (text.trim() === lastCommitted.current.trim()) return undefined;
    const timer = setTimeout(() => {
      lastCommitted.current = text.trim();
      commitRef.current(text.trim());
    }, FEED_FILTER_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [text]);

  return [text, setText] as const;
}

/**
 * Filter bar of the Community feed: period (presets or a from–to range),
 * aquarium name, category, "only my entries", free text and sort order. All
 * filters are applied server-side and live in the URL. On narrow screens the
 * controls collapse behind a "Filter" disclosure button (aria-expanded).
 *
 * There is deliberately no filter by person: the feed never shows names or
 * e-mail addresses — the aquarium name is the public identity.
 */
export function CommunityFeedFilters({ value, onChange, onReset }: CommunityFeedFiltersProps) {
  const { t } = useTranslation();
  const panelId = useId();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useDebouncedText(value.q, (next) => onChange({ q: next }));
  const [aquarium, setAquarium] = useDebouncedText(value.aquarium, (next) => onChange({ aquarium: next }));

  const activeCount = countActiveFeedFilters(value);
  const rangeInvalid = isFeedRangeInvalid(value);
  const knownTag = value.tag === '' || (JOURNAL_TAGS as readonly string[]).includes(value.tag);

  return (
    <section className="community-filters" aria-label={t('community.filters.label')} data-testid="community-filters">
      <Button
        variant="secondary"
        className="community-filters__toggle"
        aria-expanded={open}
        aria-controls={panelId}
        onClick={() => setOpen((o) => !o)}
        data-testid="community-filters-toggle"
      >
        {activeCount > 0
          ? t('community.filters.toggleActive', { count: activeCount })
          : t('community.filters.toggle')}
      </Button>

      <div
        id={panelId}
        className={`community-filters__panel${open ? '' : ' community-filters__panel--collapsed'}`}
        data-testid="community-filters-panel"
      >
        <Grid minItemWidth="sm" gap="4">
          <Select
            label={t('community.filters.period')}
            value={value.period}
            onChange={(e: ChangeEvent<HTMLSelectElement>) => onChange({ period: e.target.value as FeedPeriod })}
            data-testid="community-filter-period"
          >
            <option value="">{t('community.filters.periodAll')}</option>
            <option value="7">{t('community.filters.period7')}</option>
            <option value="30">{t('community.filters.period30')}</option>
            <option value="90">{t('community.filters.period90')}</option>
            <option value="custom">{t('community.filters.periodCustom')}</option>
          </Select>

          {value.period === 'custom' && (
            <>
              <Input
                type="date"
                label={t('community.filters.from')}
                value={value.from}
                max={value.to || undefined}
                onChange={(e: ChangeEvent<HTMLInputElement>) => onChange({ from: e.target.value })}
                data-testid="community-filter-from"
              />
              <Input
                type="date"
                label={t('community.filters.to')}
                value={value.to}
                min={value.from || undefined}
                error={rangeInvalid ? t('community.filters.rangeInvalid') : undefined}
                onChange={(e: ChangeEvent<HTMLInputElement>) => onChange({ to: e.target.value })}
                data-testid="community-filter-to"
              />
            </>
          )}

          <Input
            type="search"
            label={t('community.filters.aquarium')}
            placeholder={t('community.filters.aquariumPlaceholder')}
            helperText={t('community.filters.aquariumHint')}
            value={aquarium}
            maxLength={100}
            onChange={(e: ChangeEvent<HTMLInputElement>) => setAquarium(e.target.value)}
            data-testid="community-filter-aquarium"
          />

          <Select
            label={t('community.filters.tag')}
            value={value.tag}
            onChange={(e: ChangeEvent<HTMLSelectElement>) => onChange({ tag: e.target.value })}
            data-testid="community-filter-tag"
          >
            <option value="">{t('community.filters.allTags')}</option>
            {JOURNAL_TAGS.map((tag) => (
              <option key={tag} value={tag}>{journalTagLabel(tag, t)}</option>
            ))}
            {/* A tag from a shared link that is not predefined stays selectable. */}
            {!knownTag && <option value={value.tag}>{value.tag}</option>}
          </Select>

          <Input
            type="search"
            label={t('community.filters.searchLabel')}
            placeholder={t('community.filters.searchPlaceholder')}
            value={q}
            maxLength={200}
            onChange={(e: ChangeEvent<HTMLInputElement>) => setQ(e.target.value)}
            data-testid="community-filter-q"
          />

          <Select
            label={t('community.filters.sort')}
            value={value.sort}
            onChange={(e: ChangeEvent<HTMLSelectElement>) => onChange({ sort: e.target.value as FeedSort })}
            data-testid="community-filter-sort"
          >
            <option value="newest">{t('community.filters.sortNewest')}</option>
            <option value="oldest">{t('community.filters.sortOldest')}</option>
          </Select>
        </Grid>

        <div className="community-filters__footer">
          <Checkbox
            label={t('community.filters.mine')}
            checked={value.mine}
            onChange={(e: ChangeEvent<HTMLInputElement>) => onChange({ mine: e.target.checked })}
            data-testid="community-filter-mine"
          />
          {activeCount > 0 && (
            <Button variant="ghost" onClick={onReset} data-testid="community-filters-reset">
              {t('community.filters.reset')}
            </Button>
          )}
        </div>
      </div>
    </section>
  );
}

export default CommunityFeedFilters;
